import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, Loader2, Search, CheckCircle, XCircle, LogIn, LogOut, UserPlus, Shield, AlertTriangle, Clock, Globe } from "lucide-react";

export default function AdminLoginLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eventFilter, setEventFilter] = useState("all");
  const [successFilter, setSuccessFilter] = useState("all");
  const [query, setQuery] = useState("");

  const fetchLogs = async () => {
    try {
      const params = {};
      if (eventFilter !== "all") params.event_type = eventFilter;
      if (successFilter !== "all") params.success = successFilter;
      if (query) params.q = query;
      const [logsRes, statsRes] = await Promise.all([
        api.get("/admin/login-logs", { params }),
        api.get("/admin/login-logs/stats"),
      ]);
      setLogs(logsRes.data);
      setStats(statsRes.data);
    } catch { setLogs([]); }
    setLoading(false);
  };

  useEffect(() => { setLoading(true); fetchLogs(); }, [eventFilter, successFilter]); // eslint-disable-line

  const handleSearch = () => { setLoading(true); fetchLogs(); };

  const eventIcons = { LOGIN: LogIn, REGISTER: UserPlus, LOGOUT: LogOut };
  const eventColors = { LOGIN: "bg-blue-100 text-blue-800", REGISTER: "bg-purple-100 text-purple-800", LOGOUT: "bg-slate-100 text-slate-600" };

  const formatTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("en-PH", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  if (loading && !stats) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="admin-login-logs-page">
      <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3 mb-1">
        <FileText className="w-8 h-8 text-court-orange" />Login Logs
      </h1>
      <p className="text-muted-foreground mb-6">Authentication activity and security monitoring</p>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-8">
          {[
            { label: "Total Events", value: stats.total, icon: FileText, color: "text-slate-500" },
            { label: "Successful", value: stats.successful, icon: CheckCircle, color: "text-green-500" },
            { label: "Failed", value: stats.failed, icon: XCircle, color: "text-red-500" },
            { label: "Logins", value: stats.logins, icon: LogIn, color: "text-blue-500" },
            { label: "Registers", value: stats.registers, icon: UserPlus, color: "text-purple-500" },
            { label: "Logouts", value: stats.logouts, icon: LogOut, color: "text-slate-400" },
            { label: "Failed Logins", value: stats.failed_logins, icon: Shield, color: "text-amber-500" },
            { label: "Failed (24h)", value: stats.recent_failed_24h, icon: AlertTriangle, color: stats.recent_failed_24h > 10 ? "text-red-600" : "text-amber-400" },
          ].map((s, i) => (
            <div key={i} className="bg-white border-2 border-slate-100 p-3" data-testid={`log-stat-${i}`}>
              <s.icon className={`w-4 h-4 ${s.color} mb-1`} />
              <p className="font-heading text-xl font-bold">{s.value}</p>
              <p className="text-[9px] font-heading uppercase tracking-[0.1em] text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="flex-1 flex gap-2">
          <Input data-testid="log-search" value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search by email, detail, or IP..." className="h-11 rounded-none border-2"
            onKeyDown={e => e.key === "Enter" && handleSearch()} />
          <Button data-testid="log-search-btn" onClick={handleSearch} className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full px-6">
            <Search className="w-4 h-4" />
          </Button>
        </div>
        <Select value={eventFilter} onValueChange={setEventFilter}>
          <SelectTrigger data-testid="event-type-filter" className="w-full md:w-40 h-11 rounded-none border-2">
            <SelectValue placeholder="All Events" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            <SelectItem value="LOGIN">Logins</SelectItem>
            <SelectItem value="REGISTER">Registers</SelectItem>
            <SelectItem value="LOGOUT">Logouts</SelectItem>
          </SelectContent>
        </Select>
        <Select value={successFilter} onValueChange={setSuccessFilter}>
          <SelectTrigger data-testid="success-filter" className="w-full md:w-40 h-11 rounded-none border-2">
            <SelectValue placeholder="All Results" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Results</SelectItem>
            <SelectItem value="true">Successful</SelectItem>
            <SelectItem value="false">Failed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <p className="text-sm text-muted-foreground mb-3">{logs.length} log entries</p>

      {/* Table */}
      <div className="bg-white border-2 border-slate-100 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-heading uppercase tracking-wider text-xs">Time</TableHead>
              <TableHead className="font-heading uppercase tracking-wider text-xs">Event</TableHead>
              <TableHead className="font-heading uppercase tracking-wider text-xs">Email</TableHead>
              <TableHead className="font-heading uppercase tracking-wider text-xs">Result</TableHead>
              <TableHead className="font-heading uppercase tracking-wider text-xs">Detail</TableHead>
              <TableHead className="font-heading uppercase tracking-wider text-xs">Role</TableHead>
              <TableHead className="font-heading uppercase tracking-wider text-xs">IP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                  <FileText className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                  No log entries found
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log, i) => {
                const Icon = eventIcons[log.event_type] || FileText;
                const evtColor = eventColors[log.event_type] || "bg-slate-100 text-slate-600";
                return (
                  <TableRow key={log.log_id || i} data-testid={`log-row-${i}`}
                    className={!log.success ? "bg-red-50/30" : ""}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatTime(log.timestamp)}</span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${evtColor} text-[10px] font-heading uppercase`}>
                        <Icon className="w-3 h-3 mr-1 inline" />{log.event_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm font-medium">{log.email}</TableCell>
                    <TableCell>
                      {log.success ? (
                        <Badge className="bg-green-100 text-green-800 text-[10px] font-heading uppercase">
                          <CheckCircle className="w-3 h-3 mr-1 inline" />Success
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-800 text-[10px] font-heading uppercase">
                          <XCircle className="w-3 h-3 mr-1 inline" />Failed
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[250px] truncate">{log.detail}</TableCell>
                    <TableCell>
                      {log.role && <Badge variant="outline" className="text-[10px] font-heading uppercase">{log.role?.replace("_", " ")}</Badge>}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">
                      <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{log.ip?.split(",")[0]?.trim()}</span>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
